<div>
  <div class="graph-wrapper tw-py-1 tw-px-1 tw-mx-auto">
    <div class="tw-relative">
      <div
        #scrollContainer
        (scroll)="onScroll()"
        (wheel)="onWheel($event)"
        tabindex="0"
        role="region"
        aria-label="Transactions heatmap, scrollable"
        class="heatmap-scroller hide-scrollbars tw-overflow-x-auto tw-overflow-y-hidden focus-visible:tw-outline-offset-[-2px]"
        [class.fade-l]="canScrollLeft()"
        [class.fade-r]="canScrollRight()"
      >
        <div class="graph tw-grid-cols-[auto_max-content] tw-gap-2 tw-p-4 tw-w-max">
          <ul class="months tw-list-none tw-p-0 tw-m-0">
            @for (m of monthLabels(); track $index) {
              <li
                class="tw-text-dt-text-headings tw-row-span-full"
                [style.grid-column-start]="m.columnStart"
              >
                {{ m.name }}
              </li>
            }
          </ul>

          <ul class="days tw-list-none tw-p-0 tw-m-0 tw-gap-[var(--square-gap)]">
            @for (d of days; track $index) {
              <li class="tw-leading-[var(--square-size)] tw-text-right tw-text-dt-text-headings">
                {{ d }}
              </li>
            }
          </ul>

          <ul
            class="squares tw-list-none tw-p-0 tw-m-0 tw-gap-[var(--square-gap)] tw-grid-flow-col tw-auto-cols-max"
          >
            @for (square of data(); track $index) {
              <li
                class="tw-cursor-pointer tw-relative tw-rounded-[3px] tw-w-[var(--square-size)] tw-h-[var(--square-size)] tw-shadow-square hover:tw-rounded-[3px] hover:tw-border-2 hover:tw-border-solid tw-border-tealgray-400"
                [attr.data-level]="square.level"
                [style.grid-row]="square.dayOfWeek + 1"
              >
                <span
                  [class.tooltip-end]="$index >= endAlignedTooltipStartIndex()"
                  class="tooltip tw-absolute tw-bottom-[calc(var(--square-size)+10px)] tw-left-2/4 tw--translate-x-1/2 tw-bg-neutral-12 tw-text-white tw-px-1 tw-py-2 tw-rounded-md tw-text-xs tw-whitespace-nowrap tw-pointer-events-none tw-z-50 tw-opacity-0 tw-invisible tw-duration-200"
                >
                  {{ square.dateString }} : {{ square.transactionCount }}
                </span>
              </li>
            }
          </ul>
        </div>
      </div>

      @if (canScrollLeft()) {
        <button
          #scrollLeftButton
          type="button"
          (click)="scrollByStep(-1)"
          aria-label="Scroll heatmap left"
          class="tw-absolute tw-left-1 tw-top-1/2 tw--translate-y-1/2 tw-flex tw-h-7 tw-w-7 sm:tw-h-9 sm:tw-w-9 tw-box-border tw-items-center tw-justify-center tw-rounded-full tw-overflow-hidden tw-bg-dt-surface-card tw-text-dt-icon-action tw-shadow-elevation-card hover:tw-bg-dt-surface-action-hover-alt"
        >
          <icon [name]="'left-arrow'" [size]="4" [disableArrowOverride]="true"></icon>
        </button>
      }
      @if (canScrollRight()) {
        <button
          #scrollRightButton
          type="button"
          (click)="scrollByStep(1)"
          aria-label="Scroll heatmap right"
          class="tw-absolute tw-right-1 tw-top-1/2 tw--translate-y-1/2 tw-flex tw-h-7 tw-w-7 sm:tw-h-9 sm:tw-w-9 tw-box-border tw-items-center tw-justify-center tw-rounded-full tw-overflow-hidden tw-bg-dt-surface-card tw-text-dt-icon-action tw-shadow-elevation-card hover:tw-bg-dt-surface-action-hover-alt"
        >
          <icon [name]="'right-arrow'" [size]="4" [disableArrowOverride]="true"></icon>
        </button>
      }
    </div>

    <!-- Legend + Info row (separate siblings) -->
    <div class="tw-flex tw-items-center tw-justify-end tw-gap-3 tw-mt-3 tw-mr-3">
      <!-- Fewer -> More, the same six steps the squares use -->
      <div
        class="tw-flex tw-items-center tw-gap-1 tw-text-xs tw-text-dt-text-secondary"
        data-testid="heatmap-legend"
      >
        <span>Fewer</span>
        @for (n of [0, 1, 2, 3, 4, 5]; track n) {
          <span
            class="tw-inline-block tw-w-[var(--square-size)] tw-h-[var(--square-size)] tw-rounded-[3px] tw-shadow-square"
            [attr.data-level]="n"
          ></span>
        }
        <span>More</span>
      </div>
      <!-- Info icon block (separate from legend) -->
      <div class="info-sprite-wrap tw-relative">
        <svg class="tw-h-5 tw-w-5 md:tw-h-4 md:tw-w-4">
          <use xlink:href="images/sprite.svg#info-tooltip" />
        </svg>

        <!-- Popout -->
        <div
          class="info-sprite-popover tw-absolute tw-right-0 tw-top-8 tw-translate-y-1 tw-bg-dt-surface-elevated tw-text-dt-text-headings tw-px-2 tw-py-[10px] tw-text-xs tw-border tw-border-neutral-800 tw-rounded-lg tw-w-max tw-max-w-[320px] tw-shadow-tips tw-pointer-events-none tw-opacity-0 tw-invisible tw-z-50 tw-duration-150"
        >
          Q-values represent yearly percentiles calculated from 365 daily transactions.
          <br />
          <span class="tw-basis-auto tw-mb-0 tw-font-bold">Color levels (percentile-based): </span>
          <span class="tw-flex tw-items-center tw-gap-1 tw-flex-wrap">
            <span
              class="tw-inline-block tw-w-[var(--square-size)] tw-h-[var(--square-size)] tw-my-1 tw--mr-0.5 tw-rounded-[3px] tw-border tw-border-neutral-800 tw-bg-[var(--color-0)]"
            ></span
            >0
            <span
              class="tw-inline-block tw-w-[var(--square-size)] tw-h-[var(--square-size)] tw-ml-1.5 tw-my-1 tw--mr-0.5 tw-rounded-[3px] tw-border tw-border-neutral-800 tw-bg-[var(--color-1)]"
            ></span
            >≤Q20
            <span
              class="tw-inline-block tw-w-[var(--square-size)] tw-h-[var(--square-size)] tw-ml-1.5 tw-my-1 tw--mr-0.5 tw-rounded-[3px] tw-border tw-border-neutral-800 tw-bg-[var(--color-2)]"
            ></span
            >Q20-Q40
            <span
              class="tw-inline-block tw-w-[var(--square-size)] tw-h-[var(--square-size)] tw-ml-1.5 tw-my-1 tw--mr-0.5 tw-rounded-[3px] tw-border tw-border-neutral-800 tw-bg-[var(--color-3)]"
            ></span
            >Q40-Q60
            <span
              class="tw-inline-block tw-w-[var(--square-size)] tw-h-[var(--square-size)] tw-ml-1.5 tw-my-1 tw--mr-0.5 tw-rounded-[3px] tw-border tw-border-neutral-800 tw-bg-[var(--color-4)]"
            ></span
            >Q60-Q80
            <span
              class="tw-inline-block tw-w-[var(--square-size)] tw-h-[var(--square-size)] tw-ml-1.5 tw-my-1 tw--mr-0.5 tw-rounded-[3px] tw-border tw-border-neutral-800 tw-bg-[var(--color-5)]"
            ></span
            >>Q80
          </span>
        </div>
      </div>
    </div>
  </div>
</div>
